<div class="h-full">
  <div class="flex h-full flex-col gap-3">
    <ng-container *ngTemplateOutlet="tableControls" />

    @if (isLoading && hasLoadingAnimation) {
      <ng-container *ngTemplateOutlet="showLoader" />
    }

    @if (!isLoading && groupedData && !isEmpty) {
      <ng-container *ngTemplateOutlet="table" />
    }

    @if (!isLoading && isEmpty) {
      <ng-container *ngTemplateOutlet="empty" />
    }

    @if (!isLoading && !pagingService) {
      <footer class="flex items-center gap-3">
        <div class="flex grow items-center justify-between">
          <p class="text-left">
            Last Updated: {{ lastUpdated | date: "medium" }}
            (Local Time)
          </p>
          <div class="flex flex-row">
            <ng-container *ngTemplateOutlet="customActionFooterContentRef" />
            @if (dataLength) {
              Items: {{ dataLength }}
            }
          </div>
        </div>
      </footer>
    }

    <!-- pagination -->
    @if (pagingService && data?.length) {
      <ng-container *ngTemplateOutlet="paginator" />
    }
  </div>
</div>

<ng-template #paginator>
  <div class="flex items-center gap-2">
    <div class="ml-auto flex items-center gap-3">
      <span>Items per Page:</span>
      <select
        class="w-fit pr-8"
        [(ngModel)]="selectedPageSize"
        (change)="setPageSize()"
      >
        <option selected disabled [value]="null">Default</option>
        @for (pageSize of pageSizes; track pageSize) {
          <option [value]="pageSize">
            {{ pageSize }}
          </option>
        }
      </select>
      <p>
        {{ pagingService.firstItem }} - {{ pagingService.lastItem }} of
        {{ pagingService.totalItems }}
      </p>
    </div>
    <ag1-button
      (clickEvent)="previousPage()"
      [disabled]="!pagingService.hasPrevPage"
      variant="tertiary"
      [classOverrides]="'p-1 min-h-0'"
    >
      <svg-icon src="/assets/icons/general/chevron-left.svg" />
    </ag1-button>

    <ag1-button
      (clickEvent)="nextPage()"
      [disabled]="!pagingService.hasNextPage"
      variant="tertiary"
      [classOverrides]="'p-1 min-h-0'"
    >
      <svg-icon src="/assets/icons/general/chevron-right.svg" />
    </ag1-button>
  </div>
</ng-template>

<ng-template #tableControls>
  <!-- actions -->
  <div class="flex flex-wrap items-center gap-3">
    <!-- filters -->
    @if (hasFilters) {
      <ag1-filter-group
        (filterEvent)="applyFilters()"
        [disabled]="isLoading"
        [name]="label"
      />
    }

    <!-- search -->
    @if (hasSearch) {
      <ag1-search
        class="min-w-[22rem] lg:min-w-[34rem]"
        placeholder="Search for {{ label }}"
        [disabled]="isLoading"
        [searchFields]="searchFields"
        (searchEvent)="applySearch($event)"
      />
    }

    <!-- info -->
    @if (infoHeading && infoBody) {
      <ag1-info [heading]="infoHeading" [body]="infoBody" />
    }

    <!-- actions -->
    <div class="ml-auto flex flex-wrap items-center gap-3">
      @if (downloadable) {
        <!-- export -->
        <ag1-button
          [disabled]="isLoading || isEmpty"
          (clickEvent)="exportToXLSX()"
          tooltip="Download"
        >
          <svg-icon src="/assets/icons/general/download.svg" />
        </ag1-button>
      }

      <!-- group by -->
      @if (canGroup) {
        <ag1-button
          variant="tertiary"
          label="Group By"
          position="right"
          (clickEvent)="ag1TableGroupByPopover.toggle($event)"
          [disabled]="isLoading || isEmpty"
        >
          <svg-icon src="/assets/icons/general/group.svg" />
        </ag1-button>
        <p-popover #ag1TableGroupByPopover>
          <div class="w-72">
            <p class="p-2 text-sm font-semibold">Select fields to group on</p>
            <div
              class="hover:scrollbar-thumb-slate-600 scrollbar-thumb-slate-500 scrollbar-track-slate-50 grid max-h-64 grid-cols-1 overflow-y-auto"
            >
              @for (field of fields; track trackByField($index, field)) {
                @if (field.checked && field.groupable) {
                  <div class="mr-2 p-1.5 text-xs">
                    <label class="flex grow select-none items-center gap-2">
                      <input
                        type="checkbox"
                        [checked]="field.grouped"
                        (change)="groupedFieldChange(field)"
                      />
                      <ng-container
                        [ngTemplateOutlet]="displayName"
                        [ngTemplateOutletContext]="{ field: field }"
                      />
                    </label>
                  </div>
                }
              }
            </div>
          </div>
        </p-popover>
      }

      <!-- columns -->
      @if (editColumns) {
        <ag1-button
          variant="tertiary"
          label="Columns"
          position="right"
          (clickEvent)="ag1TableColumnsPopover.toggle($event)"
          [disabled]="isLoading || !fields?.length"
        >
          <svg-icon src="/assets/icons/general/columns.svg" />
        </ag1-button>
        <p-popover #ag1TableColumnsPopover>
          <div class="w-72">
            <p class="p-2 text-sm font-semibold">
              Customise order and visbility of columns
            </p>
            <div
              class="hover:scrollbar-thumb-slate-600 scrollbar-thumb-slate-500 scrollbar-track-slate-50 grid max-h-64 grid-cols-1 overflow-y-auto"
            >
              @for (field of fields; track field; let index = $index) {
                <div class="mr-2 text-xs">
                  <div class="flex items-center gap-3">
                    <input
                      type="text"
                      [value]="index"
                      (change)="updateColumnOrder($event, index)"
                      pattern="^\d+$"
                      class="w-8 !border-transparent p-1 hover:!border-slate-200"
                    />
                    <label class="flex grow select-none items-center gap-2">
                      <input
                        type="checkbox"
                        [(ngModel)]="field.checked"
                        (ngModelChange)="updateTable(field)"
                        [disabled]="field.grouped"
                      />
                      <ng-container
                        [ngTemplateOutlet]="displayName"
                        [ngTemplateOutletContext]="{ field: field }"
                      />
                    </label>
                  </div>
                </div>
              }
            </div>
          </div>
        </p-popover>
      }
    </div>
  </div>

  <!-- pills -->
  @if (hasFilters) {
    <ag1-pill-group />
  }
</ng-template>

<ng-template #table>
  <div class="grow overflow-auto">
    <table
      id="table"
      class="mb-2 min-w-full [&_tbody:hover_td[rowspan]]:bg-slate-100"
    >
      <thead class="select-none border-b-2 border-slate-100 bg-white">
        <tr
          class="sticky top-0 z-20 whitespace-nowrap bg-white text-left text-sm tracking-wide text-slate-800 after:absolute after:inset-0 after:-bottom-[1px] after:top-unset after:h-[1px] after:w-full after:bg-slate-200"
        >
          @if (useCustomActions || (nested && !hasGroupedFields)) {
            <th
              class="cursor-pointer p-3 font-semibold first:sticky first:left-0 first:top-0 first:bg-white first:before:absolute first:before:inset-0 first:before:left-unset first:before:h-full first:before:w-[1px] first:before:bg-slate-200"
            >
              <ng-container
                [ngTemplateOutlet]="
                  customActionHeaderContent || defaultActionHeaderContent
                "
              />
            </th>
          }
          @for (field of fields; track trackByField($index, field)) {
            @if (field.checked && !field.grouped) {
              <th
                (click)="
                  field && field?.sortable === false ? null : sortColumn(field)
                "
                class="p-3 font-semibold first:sticky first:left-0 first:top-0 first:bg-white first:before:absolute first:before:inset-0 first:before:left-unset first:before:h-full first:before:w-[1px] first:before:bg-slate-200"
                [ngClass]="
                  field && field?.sortable === false ? '' : 'cursor-pointer'
                "
              >
                <div class="flex items-center gap-1">
                  @if (sortedField === field.name) {
                    @if (sortDirection === "asc") {
                      <svg-icon src="/assets/icons/general/sort-asc.svg" />
                    }
                    @if (sortDirection === "desc") {
                      <svg-icon src="/assets/icons/general/sort-desc.svg" />
                    }
                  }
                  <ng-container
                    [ngTemplateOutlet]="displayName"
                    [ngTemplateOutletContext]="{ field: field }"
                  />
                </div>
              </th>
            }
          }
        </tr>
      </thead>

      @for (groupKey of Object.keys(groupedData); track groupKey) {
        <tbody>
          <!-- grouped fields header -->
          @if (hasGroupedFields) {
            <tr
              class="sticky left-0 top-[45px] z-20 whitespace-nowrap text-left"
            >
              <th
                class="sticky left-0 top-[45px] z-30 w-full bg-steel-blue-500 p-3 text-sm font-normal tracking-wider text-white"
                [attr.colspan]="numSelected"
              >
                <span class="sticky left-3 top-0 flex w-fit items-center">
                  @for (
                    key of groupedFieldKeys;
                    track key;
                    let keyIndex = $index
                  ) {
                    @if (keyIndex !== 0) {
                      <span class="text-slate-200"> &nbsp;and&nbsp; </span>
                    }
                    <span>{{ key | humanize }}:&nbsp;</span>
                    <span>
                      @if (groupedData[groupKey][0][key]) {
                        <ng-container
                          [ngTemplateOutlet]="
                            customCellContentRef || defaultCellContent
                          "
                          [ngTemplateOutletContext]="{
                            $implicit: groupedData[groupKey][0],
                            key: key,
                            index: rowIndex,
                            groupedData: groupedData,
                          }"
                        />
                      }
                      @if (!groupedData[groupKey][0][key]) {
                        None
                      }
                    </span>
                  }
                </span>
              </th>
            </tr>
          }
          @for (
            item of groupedData[groupKey];
            track trackByItem(rowIndex, item);
            let rowIndex = $index
          ) {
            <tr
              class="group whitespace-nowrap bg-white text-left text-sm tracking-wide text-slate-600"
            >
              @if (useCustomActions || (nested && !hasGroupedFields)) {
                <td
                  [ngClass]="[
                    expandedRowIndex === rowIndex ? '!bg-steel-blue-50' : '',
                  ]"
                  class="p-3 align-middle first:sticky first:left-0 first:z-10 first:bg-white first:before:absolute first:before:inset-0 first:before:left-unset first:before:h-full first:before:w-[1px] first:before:bg-slate-200 last:w-full group-odd:bg-white group-even:bg-slate-50 group-hover:bg-slate-100"
                >
                  <div class="flex items-center gap-2">
                    <!-- nesting -->
                    @if (nested && !hasGroupedFields) {
                      <ag1-button-small
                        variant="secondary"
                        tooltip="Expand"
                        (clickEvent)="toggleNestedData(item, rowIndex)"
                      >
                        @if (expandedRowIndex === rowIndex) {
                          <svg-icon
                            src="/assets/icons/general/chevron-up.svg"
                          />
                        }
                        @if (expandedRowIndex !== rowIndex) {
                          <svg-icon
                            src="/assets/icons/general/chevron-down.svg"
                          />
                        }
                      </ag1-button-small>
                    }
                    <!-- custom actions -->
                    @if (useCustomActions) {
                      <ng-container
                        [ngTemplateOutlet]="customActionsRef"
                        [ngTemplateOutletContext]="{
                          $implicit: item,
                          key: groupKey,
                          index: rowIndex,
                        }"
                      />
                    }
                  </div>
                </td>
              }
              @for (field of fields; track trackByField($index, field)) {
                <!-- grouped fields -->
                @if (hasGroupedFields && field.checked) {
                  <!-- cummulative quanitiy applicable -->
                  @if (groupedFieldKeys.includes("groupId")) {
                    <!-- cumulative fields -->
                    @if (cumulativeFields.includes(field.name)) {
                      @if (!field.grouped) {
                        <ng-container
                          [ngTemplateOutlet]="cumulativeTableCell"
                          [ngTemplateOutletContext]="{
                            $implicit: item,
                            key: field.name,
                            rowIndex: rowIndex,
                            items: groupedData[groupKey],
                          }"
                        />
                      }
                    }
                    <!-- non cumulative fields -->
                    @if (!cumulativeFields.includes(field.name)) {
                      @if (!field.grouped) {
                        <ng-container
                          [ngTemplateOutlet]="standardTableCell"
                          [ngTemplateOutletContext]="{
                            $implicit: item,
                            key: field.name,
                            rowIndex: rowIndex,
                          }"
                        />
                      }
                    }
                  }
                  <!-- cummulative quanitiy not applicable -->
                  @if (!groupedFieldKeys.includes("groupId")) {
                    @if (!field.grouped) {
                      <ng-container
                        [ngTemplateOutlet]="standardTableCell"
                        [ngTemplateOutletContext]="{
                          $implicit: item,
                          key: field.name,
                          rowIndex: rowIndex,
                        }"
                      />
                    }
                  }
                }
                <!-- ungrouped fields -->
                @if (!hasGroupedFields && field.checked) {
                  <ng-container
                    [ngTemplateOutlet]="standardTableCell"
                    [ngTemplateOutletContext]="{
                      $implicit: item,
                      key: field.name,
                      rowIndex: rowIndex,
                    }"
                  />
                }
              }
            </tr>
            <!-- nested table -->
            @if (expandedRowIndex === rowIndex && !hasGroupedFields) {
              <ng-container *ngTemplateOutlet="nestedTable" />
            }
          }
        </tbody>
      }
    </table>
  </div>
</ng-template>

<ng-template #defaultActionHeaderContent> Actions </ng-template>

<ng-template #displayName let-field="field">
  @if (!field?.displayName) {
    <span>
      {{ $safeNavigationMigration(field?.name) | humanize }}
    </span>
  }
  @if (field?.displayName) {
    <span>
      {{ field.displayName | humanize }}
    </span>
  }
</ng-template>

<!-- table cell templates -->
<ng-template #standardTableCell let-item let-key="key" let-rowIndex="rowIndex">
  <td
    class="p-3 align-middle first:sticky first:left-0 first:z-10 first:bg-white first:before:absolute first:before:inset-0 first:before:left-unset first:before:h-full first:before:w-[1px] first:before:bg-slate-200 last:w-full first:focus-within:z-[31] group-odd:bg-white group-even:bg-slate-50 group-hover:bg-slate-100"
    tabindex="0"
    [ngClass]="[
      item.groupId && key === 'quantityRequested'
        ? 'ag1-tooltip--decoration'
        : '',
      expandedRowIndex === rowIndex ? '!bg-steel-blue-50' : '',
    ]"
    [pTooltip]="
      'cumulative quantity with other line items with Group ID ' + item.groupId
    "
  >
    <ng-container
      [ngTemplateOutlet]="customCellContentRef || defaultCellContent"
      [ngTemplateOutletContext]="{
        $implicit: item,
        key: key,
        index: rowIndex,
        groupedData: groupedData,
      }"
    />
  </td>
</ng-template>

<ng-template
  #cumulativeTableCell
  let-item
  let-key="key"
  let-rowIndex="rowIndex"
  let-items="items"
>
  <!-- grouped item -->
  @if (item["groupId"]) {
    <!-- first row -->
    @if (rowIndex === 0) {
      <td
        class="first:before:left-unset' p-3 align-middle first:sticky first:left-0 first:z-10 first:bg-white first:before:absolute first:before:inset-0 first:before:h-full first:before:w-[1px] first:before:bg-slate-200 last:w-full"
        [attr.rowspan]="items.length"
      >
        <ng-container
          [ngTemplateOutlet]="customCellContentRef || defaultCellContent"
          [ngTemplateOutletContext]="{
            $implicit: item,
            key: key,
          }"
        />
      </td>
    }
    <!-- remaining rows -->
    @if (rowIndex !== 0) {
      <!-- dont render anything  -->
    }
  }

  <!-- ungrouped item -->
  @if (!item["groupId"]) {
    <ng-container
      [ngTemplateOutlet]="standardTableCell"
      [ngTemplateOutletContext]="{
        $implicit: item,
        key: key,
        rowIndex: rowIndex,
      }"
    />
  }
</ng-template>

<ng-template #defaultCellContent let-item let-key="key">
  {{ item[key] }}
</ng-template>

<!-- table states -->
<ng-template #empty>
  <div class="grid h-full w-full place-items-center gap-2 p-5">
    <div class="flex items-center gap-3">
      <svg-icon
        svgClass="w-10 h-10 text-steel-blue-500"
        src="/assets/icons/general/warning.svg"
      />
      <div class="flex flex-col gap-1">
        <h2 class="text-lg font-semibold">
          {{ emptyMessage }}
        </h2>
        @if (hasFilters) {
          <p>Try changing the applied filters.</p>
        }
      </div>
    </div>
  </div>
</ng-template>

<ng-template #showLoader>
  <div class="grid h-full w-full place-items-center gap-2 p-5">
    <ag1-loader />
  </div>
</ng-template>

<ng-template #nestedTable>
  <tr>
    <td
      class="rounded-b-lg !bg-steel-blue-50 p-3"
      [attr.colspan]="fields.length + 1"
    >
      @if (isNestedLoading) {
        <ng-container *ngTemplateOutlet="nestedLoading" />
      }
      @if (!isNestedLoading && !isNestedEmpty) {
        <table
          id="table"
          class="mb-2 min-w-full [&_tbody:hover_td[rowspan]]:bg-slate-100"
        >
          <thead class="select-none border-b-2 border-slate-100 bg-white">
            <tr
              class="sticky top-0 z-20 whitespace-nowrap bg-white text-left text-sm tracking-wide text-slate-800 after:absolute after:inset-0 after:-bottom-[1px] after:top-unset after:h-[1px] after:w-full after:bg-slate-200"
            >
              @for (nestedField of nestedFields; track nestedField) {
                <th
                  class="p-3 font-semibold first:sticky first:left-0 first:top-0 first:bg-white first:before:absolute first:before:inset-0 first:before:left-unset first:before:h-full first:before:w-[1px] first:before:bg-slate-200"
                >
                  <div class="flex items-center gap-1">
                    {{ nestedField | humanize }}
                  </div>
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (item of nestedData; track item; let rowIndex = $index) {
              <tr
                class="group whitespace-nowrap bg-white text-left text-sm tracking-wide text-slate-600"
              >
                @for (nestedField of nestedFields; track nestedField) {
                  <td
                    class="p-3 align-middle first:sticky first:left-0 first:z-10 first:bg-white first:before:absolute first:before:inset-0 first:before:left-unset first:before:h-full first:before:w-[1px] first:before:bg-slate-200 last:w-full first:focus-within:z-[31] group-odd:bg-white group-even:bg-slate-50 group-hover:bg-slate-100"
                  >
                    {{ item[nestedField] }}
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      }
      @if (!isNestedLoading && isNestedEmpty) {
        <ng-container *ngTemplateOutlet="nestedEmpty" />
      }
    </td>
  </tr>
</ng-template>

<ng-template #nestedLoading>
  <ag-shimmer-loader />
</ng-template>

<ng-template #nestedEmpty>
  <div class="grid h-full w-full gap-2 p-5">
    <div class="flex items-center gap-3">
      <svg-icon
        svgClass="w-10 h-10 text-steel-blue-500"
        src="/assets/icons/general/warning.svg"
      />
      <div class="flex flex-col gap-1">
        <h2 class="text-lg font-semibold">
          {{ emptyMessage }}
        </h2>
        <p>This shipment has no shipment line items associated with it.</p>
      </div>
    </div>
  </div>
</ng-template>
